import { FC, ReactNode } from 'react';
import {
    RoutingRuleAssignmentTargetType,
    RoutingRuleBillAggregationType,
    useRepositoryQuery,
} from '@mews-commander/data-access';
import { lt, useFeatureFlag } from '@mews/framework';
import { createHandler } from '@mews/std';
import { UnicodeEntity } from '@mews/enums';
import { FeatureFlag } from '@mews-commander/enums';

const getAggregationTypeDescription = createHandler<RoutingRuleBillAggregationType, ReactNode>({
    [RoutingRuleBillAggregationType.AggregateAll]: lt('RoutingRulesCompanyBillAggregateAllDescription'),
    [RoutingRuleBillAggregationType.AggregateByCustomer]: lt('RoutingRulesCompanyBillAggregateByCustomerDescription'),
    [RoutingRuleBillAggregationType.OnePerReservation]: lt('RoutingRulesCompanyBillOnePerReservationDescription'),
    [RoutingRuleBillAggregationType.AggregatePerReservationGroup]: lt(
        'RoutingRulesCompanyBillAggregatePerReservationGroupDescription'
    ),
});

interface GuestProfileTargetProps {
    id?: string;
}

const GuestProfileTarget: FC<GuestProfileTargetProps> = ({ id }) => {
    const granularRoutingRulesConditionsExtensionEnabled = useFeatureFlag(
        FeatureFlag.granularRoutingRulesConditionsExtensionEnabled
    );

    const { data: guestProfileData } = useRepositoryQuery(
        'customers',
        'getAll',
        { customerIds: id ? [id] : [] },
        {
            enabled: granularRoutingRulesConditionsExtensionEnabled && !!id,
            select: (result) => (result && result.length === 1 ? result[0] : null),
        }
    );

    return guestProfileData ? (
        <>{`${guestProfileData.lastName}${UnicodeEntity.NoBreakSpace}${guestProfileData.firstName}`}</>
    ) : null;
};

export const getTargetDescription = ({
    assignmentType,
    aggregationType,
    granularRoutingRulesConditionsExtensionEnabled,
    guestProfileTarget,
    compact = false,
}: {
    assignmentType: RoutingRuleAssignmentTargetType;
    aggregationType: RoutingRuleBillAggregationType;
    granularRoutingRulesConditionsExtensionEnabled: boolean;
    guestProfileTarget?: string;
    compact?: boolean;
}): ReactNode[] | null => {
    if (granularRoutingRulesConditionsExtensionEnabled) {
        if (compact) {
            return [getAggregationTypeDescription(aggregationType)];
        }

        const description: ReactNode[] = [];

        if (guestProfileTarget) {
            description.push(<GuestProfileTarget id={guestProfileTarget} />);
        }

        if (assignmentType === RoutingRuleAssignmentTargetType.CompanyAsDetails) {
            description.push(lt('RoutingRulesGuestBillCompanyAsDetailsDescription'));
        }

        description.push(getAggregationTypeDescription(aggregationType));

        return description;
    }

    if (assignmentType === RoutingRuleAssignmentTargetType.NoCompany) {
        return null;
    }

    if (assignmentType === RoutingRuleAssignmentTargetType.CompanyAsDetails) {
        return [lt('RoutingRulesGuestBillCompanyAsDetailsDescription')];
    }

    return [getAggregationTypeDescription(aggregationType)];
};

export const getTargetTitle = createHandler<RoutingRuleAssignmentTargetType, React.ReactNode>({
    [RoutingRuleAssignmentTargetType.CompanyAsDetails]: lt('RoutingRulesGuestBill'),
    [RoutingRuleAssignmentTargetType.NoCompany]: lt('RoutingRulesGuestBill'),
    [RoutingRuleAssignmentTargetType.CompanyAsOwner]: lt('RoutingRulesCompanyBill'),
});
